// Coordinator dashboard — SR queue + stat tiles. DESIGN-SYSTEM.md §Layout Shell.

import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { SrStatusBadge } from "../components/SrStatusBadge.js";
import { RiskBadge } from "../components/RiskBadge.js";

interface SrItem {
  _id:               string;
  status:            string;
  service_type:      string;
  scheduled_datetime?: string;
  risk_score_band?:  string | null;
}

interface SrListResponse { data: SrItem[]; total: number; page: number; limit: number; }

const ACTIVE = "new,assigned,scheduled,in_progress";

function useSrQueue(): ReturnType<typeof useQuery<SrListResponse>> {
  return useQuery<SrListResponse>({
    queryKey: ["sr-queue"],
    queryFn:  () => apiFetch<SrListResponse>(`/sr?status=${ACTIVE}&limit=30`),
    refetchInterval: 30_000,
  });
}

function StatTile({ label, value, accent }: { label: string; value: number; accent?: string }): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ?? "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function SrTableRow({ sr }: { sr: SrItem }): JSX.Element {
  const dateStr = sr.scheduled_datetime
    ? new Date(sr.scheduled_datetime).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })
    : "—";
  return (
    <tr className="hover:bg-navy-50 transition-colors cursor-pointer">
      <td className="py-3 pr-4"><SrStatusBadge status={sr.status} /></td>
      <td className="py-3 pr-4 text-slate-700">{sr.service_type.replace(/_/g, " ")}</td>
      <td className="py-3 pr-4 text-slate-500">{dateStr}</td>
      <td className="py-3 pr-4"><RiskBadge band={sr.risk_score_band ?? null} /></td>
    </tr>
  );
}

function SrTable({ items }: { items: SrItem[] }): JSX.Element {
  if (items.length === 0) return <p className="py-8 text-center text-sm text-slate-400">No active service requests.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
            <th className="py-3 pr-4 font-medium">Status</th>
            <th className="py-3 pr-4 font-medium">Service type</th>
            <th className="py-3 pr-4 font-medium">Scheduled</th>
            <th className="py-3 pr-4 font-medium">Risk</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">{items.map((sr) => <SrTableRow key={sr._id} sr={sr} />)}</tbody>
      </table>
    </div>
  );
}


export function DashboardPage(): JSX.Element {
  const { data, isLoading, isError } = useSrQueue();
  const items      = data?.data ?? [];
  const unassigned = items.filter((s) => s.status === "new").length;
  const inProgress = items.filter((s) => s.status === "in_progress").length;
  const riskAlerts = items.filter((s) => s.risk_score_band === "Red" || s.risk_score_band === "Critical").length;
  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Active SRs"  value={items.length} />
          <StatTile label="Unassigned"  value={unassigned}  accent={unassigned > 0 ? "text-amber-600" : undefined} />
          <StatTile label="In Progress" value={inProgress} />
          <StatTile label="Risk Alerts" value={riskAlerts}  accent={riskAlerts > 0 ? "text-red-600" : undefined} />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Active Service Requests</h2>
          {isLoading && <p className="py-8 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>}
          {isError   && <p role="alert" className="py-4 text-center text-sm text-red-600">Failed to load service requests.</p>}
          {!isLoading && !isError && <SrTable items={items} />}
        </div>
      </div>
    </Shell>
  );
}
